import {
  createPlanningCffsApi,
  GeoSpatialFileType,
  getPlanningCffsApi,
  locutusCreateBoundaryApi,
  locutusCreateRxApi,
  PlanAsset,
  PlanAssetCreateError,
  type PlanAssetCreateErrorResult
} from 'api-validations/locutus'
import { MeasureSystem } from 'api-validations/shared'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { invalidationKeysFor, useApiClient, useQueryClient } from 'shared-ui/api-provider'
import { type Cff, cffKey } from 'shared-ui/operation-wide-data'
import { type ApiClient, Async } from 'utils'

import { CffTargetStatus } from '../lib/planUpload/planEntry'
import {
  type CreatableRow,
  CreateFailure,
  CreateStatus,
  type EntryOutcome
} from '../lib/planUpload/planUploadReducer'

/**
 * Each create carries a whole asset's GeoJSON, so this bounds how much is on the wire at once.
 */
const CREATE_CONCURRENCY = 4

/**
 * Boundaries finish storing after the create responds, so an immediate refetch can be stale.
 */
const ASYNC_STORAGE_DELAY_MS = 3_000

/**
 * Registers the new fields and reads back their ids.
 */
const registerFields = async ({
  apiClient,
  orgCode,
  newFields
}: {
  apiClient: ApiClient
  orgCode: string
  newFields: readonly Cff[]
}): Promise<Record<string, string | undefined>> => {
  if (newFields.length === 0) return {}

  await apiClient.callApi(createPlanningCffsApi, { orgCode }, { fields: [...newFields] })
  const { items } = await apiClient.callApi(getPlanningCffsApi, { orgCode })

  return Object.fromEntries(items.map((field) => [cffKey(field), field.id]))
}

const createAsset = async ({
  apiClient,
  orgCode,
  row,
  fieldId
}: {
  apiClient: ApiClient
  orgCode: string
  row: CreatableRow
  fieldId: string
}): Promise<string> => {
  const { entry, name } = row

  if (entry.assetType === PlanAsset.Boundary) {
    const { items } = await apiClient.callApi(
      locutusCreateBoundaryApi,
      { orgCode },
      {
        fieldId,
        name: name.trim(),
        type: GeoSpatialFileType.GeoJSON,
        boundary: entry.boundary,
        inverted: entry.inverted
      }
    )
    return items[0].id
  }

  const { items } = await apiClient.callApi(
    locutusCreateRxApi,
    { orgCode },
    {
      fieldId,
      name: name.trim(),
      applicationType: entry.applicationType,
      // Crusher already converted the rates to imperial; anything else converts them twice.
      measureSystem: MeasureSystem.Imperial,
      type: GeoSpatialFileType.GeoJSON,
      prescription: entry.prescription
    }
  )
  return items[0].id
}

const toFailure = (error: PlanAssetCreateErrorResult): EntryOutcome => {
  switch (error.type) {
    case PlanAssetCreateError.DuplicateName:
      return { status: CreateStatus.Failed, failure: CreateFailure.DuplicateName }
    case PlanAssetCreateError.TooLarge:
      return { status: CreateStatus.Failed, failure: CreateFailure.TooLarge }
    case PlanAssetCreateError.Other:
      return {
        status: CreateStatus.Failed,
        failure: CreateFailure.Rejected,
        message: error.message
      }
  }
}

/**
 * Creates every reviewed asset, registering new fields first. Each row settles on its own, so
 * one rejected asset does not block the rest.
 */
export const useCreatePlanAssets = () => {
  const orgCode = useOrgCodeOrThrow()
  const apiClient = useApiClient()
  const queryClient = useQueryClient()

  return async ({
    rows,
    newFields,
    onSettled
  }: {
    rows: readonly CreatableRow[]
    newFields: readonly Cff[]
    onSettled: (entryId: string, outcome: EntryOutcome) => void
  }) => {
    // Rows bound for existing-field B still import if new-field A can't be registered.
    const fieldIds = await registerFields({ apiClient, orgCode, newFields }).catch(
      (error: unknown): Record<string, string | undefined> => {
        console.warn(error)
        return {}
      }
    )

    await Async.mapConcurrent(
      [...rows],
      async (row) => {
        const fieldId =
          row.target.status === CffTargetStatus.Existing
            ? row.target.fieldId
            : fieldIds[cffKey(row.target.cff)]

        if (fieldId === undefined) {
          onSettled(row.entry.id, {
            status: CreateStatus.Failed,
            failure: CreateFailure.UnknownField
          })
          return
        }

        onSettled(row.entry.id, { status: CreateStatus.Creating })

        await createAsset({ apiClient, orgCode, row, fieldId }).then(
          (createdId) => onSettled(row.entry.id, { status: CreateStatus.Created, createdId }),
          (error: PlanAssetCreateErrorResult) => onSettled(row.entry.id, toFailure(error))
        )
      },
      { concurrency: CREATE_CONCURRENCY, throwOnError: false }
    )

    const refresh = () => {
      for (const definition of [
        locutusCreateBoundaryApi,
        locutusCreateRxApi,
        createPlanningCffsApi
      ])
        for (const queryKey of invalidationKeysFor(definition, { orgCode }))
          queryClient.invalidateQueries({ queryKey })
    }

    refresh()
    setTimeout(refresh, ASYNC_STORAGE_DELAY_MS)
  }
}
